import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { INVOICE_FIELDS } from "../nativeBusinessExecution";
import { assertReportAdmission, validateConditionalDefinition } from "../nativeConditions.mjs";
import { createInternalTypedExecution } from "../nativeTypedExecution.mjs";
import { resolveNativeContentPlan, resolveNativeLayoutPlan } from "../nativeLayout.mjs";
import { asArray, asRecord, cloneValue, type JsonRecord } from "../designer/reportDesignerModel";
import { DESIGNER_SAMPLES } from "../designer/samplePayloads";

export type TemplateCategory = "Invoicing" | "Time and expenses" | "Brokerage operations" | "Management summaries";
export type ReportTemplate = Readonly<{
  id: string; name: string; description: string; category: TemplateCategory; intendedUse: string;
  datasetFamilies: readonly string[]; requiredCapabilities: readonly string[]; definitionVersion: "reports-v3.1";
  previewReference: string; revision: number; classification: "built-in"; tags: readonly string[];
  origin: string; limitations: readonly string[]; definition: FormattedInvoicePayload;
}>;
export type TemplateProvenance = Readonly<{ templateId: string; templateRevision: number; origin: string }>;
export type TemplateInstance = { payload: FormattedInvoicePayload; provenance: TemplateProvenance };
export type TemplateCompatibility = { supported: boolean; messages: string[]; persistence: "eligible" | "internal-only" };
const instances = new WeakMap<object, string>();
export function isIssuedTemplatePayload(value: object, templateId?: string): boolean {
  return process.env.NODE_ENV !== "production" && instances.has(value) && (templateId === undefined || instances.get(value) === templateId);
}

function freezeTree<T>(value: T): T {
  if (value && typeof value === "object") {
    Object.values(value).forEach(freezeTree);
    Object.freeze(value);
  }
  return value;
}
const sampleLines = [
  { landman: "Synthetic Landman A", project: "Synthetic Prospect North", workDate: "2024-03-09", description: "Title review", billingCategory: "Title", county: "Synthetic County", quantity: 1, rate: 120, amount: 130 },
  { landman: "Synthetic Landman B", project: "Synthetic Prospect South", workDate: "2024-03-10", description: "Lease review", billingCategory: "Leasing", county: "Synthetic County", quantity: 0.5, rate: 120, amount: 60 },
  { landman: "Synthetic Landman A", project: "Synthetic Prospect North", workDate: "2024-11-02", description: "Curative review", billingCategory: "Curative", county: "Synthetic County", quantity: 1.5, rate: 100, amount: 169.05 },
  { landman: "Synthetic Landman B", project: "Synthetic Prospect South", workDate: "2024-11-03", description: "Document research", billingCategory: "Title", county: "Synthetic County", quantity: 2, rate: 120, amount: 245 },
];
function sourceInvoice(): FormattedInvoicePayload & JsonRecord {
  const existing = DESIGNER_SAMPLES.find(sample => sample.id === "native-grouped-draft-invoice-v31");
  if (!existing) throw new Error("The approved native grouped invoice starter is missing.");
  const source: FormattedInvoicePayload & JsonRecord = cloneValue(existing.payload);
  source.assets = cloneValue(DESIGNER_SAMPLES.find(sample => sample.id === "native-professional-invoice-v31")!.payload.assets);
  source.datasets = {
    invoiceHeader: {
      companyName: "Synthetic Brokerage", companyAddressLine1: "Synthetic office", companyAddressLine2: "Synthetic location",
      companyContact: "billing@example.test", clientName: "Synthetic Client", clientAddressLine1: "Synthetic client office",
      clientAddressLine2: "Synthetic client location", attentionName: "Synthetic approver", invoiceState: "unbilled",
      invoiceNumber: null, invoiceDate: "2024-11-03", periodStart: "2024-03-09", periodEnd: "2024-11-03",
      periodCovered: "03/09/2024 - 11/03/2024", dueTerms: "", notes: "",
    },
    invoiceLines: cloneValue(sampleLines),
    invoiceTotals: { days: 5, professionalServices: 570, expenses: 34.05, amountDue: 629.05 },
  };
  source.data = { datasets: Object.entries(INVOICE_FIELDS).map(([id, fields]) => ({
    id, kind: id === "invoiceLines" ? "collection" : "single", source: id === "invoiceLines" ? "invoiceTimeEntries" : "invoice",
    fields: fields.map(field => ({ ...cloneValue(field), nullable: field.nullable ?? true })),
  })) };
  source.definitionVersion = 1;
  delete source.revision;
  delete source.overrideOf;
  const layout = asRecord(source.layout);
  layout.requiredFeatures = [];
  const bands = asArray(layout.bands).map(asRecord);
  const detail = bands.find(band => band.id === "detail")!;
  detail.id = "details";
  const table = asArray(detail.elements).map(asRecord).find(element => element.type === "table")!;
  table.id = "records";
  table.emptyText = "No authorized records for this report.";
  const total = bands.find(band => band.id === "fullInvoiceTotal")!;
  total.id = "due";
  asArray(total.elements).map(asRecord).find(element => element.id === "amountDueLabel")!.text = "Authoritative invoice amount due";
  asArray(total.elements).map(asRecord).find(element => element.id === "amountDueNote")!.text = "Source total; not recomputed from displayed lines.";
  asArray(total.elements).push(
    { id: "sourceLaborLabel", type: "text", text: "Source labor", x: 0, y: 44, width: 90, height: 16, style: { fontSize: 9 } },
    { id: "sourceLabor", type: "field", binding: "invoiceTotals.professionalServices", format: "money", x: 95, y: 44, width: 90, height: 16, style: { fontSize: 9 } },
    { id: "sourceExpensesLabel", type: "text", text: "Source expenses", x: 220, y: 44, width: 100, height: 16, style: { fontSize: 9 } },
    { id: "sourceExpenses", type: "field", binding: "invoiceTotals.expenses", format: "money", x: 325, y: 44, width: 90, height: 16, style: { fontSize: 9 } },
    { id: "presentationNote", type: "text", text: "", x: 0, y: 64, width: 350, height: 16 },
  );
  total.height = 82;
  return source;
}
function mainTable(source: FormattedInvoicePayload): JsonRecord {
  return asArray(asRecord(asArray(asRecord(source.layout).bands).map(asRecord).find(band => band.id === "details")).elements).map(asRecord).find(element => element.id === "records")!;
}
function column(id: string, label: string, width: number, format?: string): JsonRecord {
  return { id, label, binding: id, width, ...(format ? { format, horizontalAlign: "right" } : {}) };
}
function summary(scope: "group" | "report", label: string): JsonRecord {
  return { cells: [
    { columnId: "workDate", value: label, colSpan: 3 },
    ...["quantity", "labor", "lineExpenses", "amount"].map(columnId => ({
      columnId, aggregate: { fn: "sum", field: columnId, scope }, format: columnId === "quantity" ? "number" : "money",
    })),
  ] };
}
function definition(kind: "invoice" | "time" | "activity" | "billing", title: string): FormattedInvoicePayload {
  const source = sourceInvoice(), layout = asRecord(source.layout), table = mainTable(source);
  source.definitionId = `builtin-${kind}-v1`;
  source.metadata = { reportName: title, sampleDataNotice: "Synthetic illustration only. No customer data or business retrieval." };
  table.columns = [
    column("workDate", "Date", 58, "date"), column("landman", "Landman", 80), { ...column("presentationDescription", "Description", 142), binding: "description" },
    column("quantity", "Days", 40, "number"),
    { id: "labor", label: "Labor", width: 70, format: "money", expr: { op: "mul", args: [{ field: "quantity" }, { field: "rate" }] } },
    { id: "lineExpenses", label: "Line expenses", width: 70, format: "money", expr: { op: "sub", args: [{ field: "amount" }, { field: "labor" }] } },
    column("amount", "Line total", 80, "money"),
  ];
  table.footer = summary("report", "Line subtotal");
  table.groups = [{
    id: "byLandman", field: kind === "billing" ? "project" : "landman", sort: "asc",
    header: { label: kind === "billing" ? "Prospect" : "Landman", height: 22, style: { bold: true, backgroundColor: "#eef4ff" } },
    footer: summary("group", "Group subtotal"),
    pagination: { keepHeaderWithFirstRow: true, keepFooterWithLastRow: true, repeatHeaderOnContinuation: true, spacingAfter: 8 },
  }];
  if (kind === "billing") {
    function summaryHeadings(value: unknown) {
      if (!value || typeof value !== "object") return;
      if (Array.isArray(value)) { value.forEach(summaryHeadings); return; }
      const node = asRecord(value);
      if (node.id === "invoiceTitle") { node.text = "SUMMARY"; node.style = { ...asRecord(node.style), fontSize: 14 }; }
      if (node.id === "draftNotice") node.text = "BILLING OVERVIEW";
      Object.values(node).forEach(summaryHeadings);
    }
    summaryHeadings(layout);
    for (const band of asArray(layout.bands).map(asRecord)) {
      if (band.role === "pageHeader") asRecord(asArray(band.elements)[0]).text = title;
      if (band.role === "pageFooter") asRecord(asArray(band.elements)[0]).text = "Invoice-backed billing presentation. Not a payment instruction.";
    }
  }
  if (kind === "invoice" || kind === "billing") return source;
  const fields = kind === "activity" ? ["workDate", "landman", "project", "description", "billingCategory", "quantity"] : INVOICE_FIELDS.invoiceLines.map(field => field.path);
  if (kind === "activity") {
    source.datasets.invoiceLines = sampleLines.map(row => Object.fromEntries(Object.entries(row).filter(([key]) => fields.includes(key))));
    asRecord(source.data).datasets = asArray(asRecord(source.data).datasets).map(asRecord).filter(dataset => dataset.id !== "invoiceTotals").map(dataset =>
      dataset.id === "invoiceLines" ? { ...dataset, fields: asArray(dataset.fields).filter(field => fields.includes(String(asRecord(field).path))) } : dataset);
    table.columns = [column("workDate", "Date", 65, "date"), column("landman", "Landman", 90), column("project", "Prospect", 105),
      { ...column("presentationDescription", "Activity", 150), binding: "description" }, column("billingCategory", "Category", 80), column("quantity", "Days", 50, "number")];
    table.groups = [{ id: "byProspect", field: "project", header: { label: "Prospect" }, footer: { cells: [
      { columnId: "presentationDescription", value: "Displayed days" }, { columnId: "quantity", aggregate: { fn: "sum", field: "quantity", scope: "group" }, format: "number" },
    ] } }];
    table.footer = { cells: [{ columnId: "presentationDescription", value: "Displayed activity count" },
      { columnId: "quantity", aggregate: { fn: "count", field: "quantity", scope: "report" }, format: "number" }] };
    layout.parameters = [{ id: "minimumDays", label: "Minimum days", description: "Already authorized activity rows only.", type: "number", required: true, nullable: false, defaultValue: 0, min: 0, max: 1000 }];
    layout.presentationFilters = [{ id: "minimumDays", tableId: "records", when: { typed: { call: "gte", args: [
      { call: "coalesce", args: [{ field: "quantity", scope: "detail" }, { const: 0 }] }, { param: "minimumDays" },
    ] } } }];
  } else {
    layout.parameters = [{ id: "reportLabel", label: "Presentation title", description: "Does not alter source time, rates or amounts.", type: "string", required: true, nullable: false, defaultValue: title, maxLength: 100 }];
  }
  delete source.datasets.invoiceTotals;
  layout.requiredFeatures = ["typed-expressions-v1", "report-parameters-v1"];
  layout.bands = asArray(layout.bands).map(asRecord).filter(band => band.id !== "reportHeader" && band.id !== "due" && band.role !== "pageHeader");
  asArray(layout.bands).unshift({ id: "reportHeader", role: "reportHeader", height: 86, elements: [
    { id: "reportTitle", type: "text", x: 0, y: 0, width: 420, height: 24, ...(kind === "time" ? { expr: { typed: { param: "reportLabel" } } } : { text: title }), style: { fontSize: 17, bold: true } },
    { id: "billingPeriod", type: "field", binding: "invoiceHeader.periodCovered", x: 0, y: 30, width: 400, height: 18 },
    { id: "scopeNotice", type: "text", text: "Invoice-backed activity only - synthetic preview", x: 0, y: 55, width: 440, height: 16, style: { fontSize: 9, color: "#63758f" } },
    { id: "companyLogo", type: "image", asset: "companyLogo", x: 440, y: 0, width: 80, height: 50 },
  ] });
  for (const band of asArray(layout.bands).map(asRecord)) if (band.role === "pageFooter") {
    asArray(band.elements).map(asRecord)[0].text = "Synthetic, invoice-backed activity. Not a payment or full invoice total.";
  }
  if (kind === "time") asRecord(source.data).datasets = asArray(asRecord(source.data).datasets).filter(dataset => asRecord(dataset).id !== "invoiceTotals");
  return source;
}

const scopeLimitations = [
  "Business-data retrieval is not enabled by selecting a template.",
  "Current authorized fields are invoice-backed; no assignment, approval status, nonbillable time or worker payment is inferred.",
  "Line expenses are presentation-derived as source line amount minus quantity x client bill rate; itemized expense bindings are unavailable.",
];
const entries: Array<Pick<ReportTemplate, "id" | "name" | "category" | "description" | "tags"> & { kind: Parameters<typeof definition>[0] }> = [
  { id: "detailed-brokerage-invoice", kind: "invoice", name: "Detailed Brokerage Invoice", category: "Invoicing", description: "A branded unbilled invoice presentation with landman groups and authoritative totals.", tags: ["Invoice", "Landman groups", "Authoritative totals"] },
  { id: "landman-time-expenses", kind: "time", name: "Landman Time and Expense Report", category: "Time and expenses", description: "Invoice-backed time and line expense review, with a presentation title parameter.", tags: ["Days", "Expenses", "Parameters"] },
  { id: "brokerage-activity", kind: "activity", name: "Brokerage Activity Report", category: "Brokerage operations", description: "Prospect-grouped invoice-backed activities with a controlled minimum-days filter.", tags: ["Prospects", "Activity", "Filters"] },
  { id: "grouped-billing-summary", kind: "billing", name: "Grouped Billing Summary", category: "Management summaries", description: "Prospect billing groups, labor and line expenses, distinct from authoritative invoice due.", tags: ["Billing", "Prospects", "Summary"] },
];
export const REPORT_TEMPLATES: readonly ReportTemplate[] = freezeTree(entries.map(({ kind, ...entry }) => {
  const payload = definition(kind, entry.name);
  return {
    ...entry, intendedUse: entry.description, datasetFamilies: ["invoice / invoiceTimeEntries"],
    requiredCapabilities: asArray(asRecord(payload.layout).requiredFeatures).map(String), definitionVersion: "reports-v3.1" as const,
    previewReference: entry.id, revision: 1, classification: "built-in" as const,
    origin: "Existing native grouped draft invoice + INVOICE_FIELDS; synthetic 6F fixtures",
    limitations: scopeLimitations, definition: payload,
  };
}));

export function templateCompatibility(template: ReportTemplate): TemplateCompatibility {
  try {
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(template.id) || template.previewReference !== template.id || !Number.isSafeInteger(template.revision) || template.revision < 1) throw new Error("Invalid catalog identity, preview reference or revision.");
    if (template.definitionVersion !== "reports-v3.1" || template.definition.schemaVersion !== template.definitionVersion) throw new Error("Unsupported template definition version.");
    const admission = assertReportAdmission(template.definition, "validate");
    if (admission.requiredFeatures.includes("conditional-rules-v1")) throw new Error("Conditional template authoring requires the separately owned conditional workspace.");
    if (JSON.stringify([...admission.requiredFeatures].sort()) !== JSON.stringify([...template.requiredCapabilities].sort())) throw new Error("Template metadata does not match mandatory capabilities.");
    const validation = validateConditionalDefinition(template.definition);
    if (validation.errors.length) throw new Error(validation.errors.map(error => error.message).join("; "));
    const descriptors = asArray(asRecord(asRecord(template.definition).data).datasets).map(asRecord);
    const catalogs = new Map(descriptors.map(dataset => [String(dataset.id), new Set(asArray(dataset.fields).map(field => String(asRecord(field).path)))]));
    if (catalogs.size !== descriptors.length || catalogs.has("undefined")) throw new Error("Dataset identifiers must be unique and declared.");
    const ids = new Set<string>();
    function visit(value: unknown, dataset?: string, element = false) {
      if (!value || typeof value !== "object") return;
      if (Array.isArray(value)) { value.forEach(child => visit(child, dataset, element)); return; }
      const node = asRecord(value);
      if (element) {
        if (!["text", "field", "image", "container", "table"].includes(String(node.type))) throw new Error(`Unsupported element type ${String(node.type)}.`);
        if (typeof node.id !== "string" || ids.has(node.id)) throw new Error(`Missing or duplicate element ID ${String(node.id)}.`);
        ids.add(node.id);
      }
      const scope = typeof node.dataset === "string" ? node.dataset : node.tableId === mainTable(template.definition).id ? String(mainTable(template.definition).dataset) : dataset;
      if (scope && !catalogs.has(scope)) throw new Error(`Undeclared dataset ${scope}.`);
      const reference = typeof node.binding === "string" ? node.binding : typeof node.field === "string" ? node.field : undefined;
      if (reference) {
        const [first, ...rest] = reference.split(".");
        const datasetId = rest.length ? first : scope;
        const field = rest.length ? rest.join(".") : first;
        const calculated = asArray(mainTable(template.definition).columns).map(asRecord).some(column => column.id === field && column.expr);
        if (!datasetId || (!catalogs.get(datasetId)?.has(field) && !calculated)) throw new Error(`Unknown binding ${reference}.`);
      }
      Object.entries(node).forEach(([key, child]) => visit(child, scope, key === "elements"));
    }
    visit(template.definition.layout);
    return { supported: true, messages: [], persistence: admission.requiredFeatures.length ? "internal-only" : "eligible" };
  } catch (error) {
    return { supported: false, messages: [error instanceof Error ? error.message : "Template compatibility failed."], persistence: "internal-only" };
  }
}
export function instantiateTemplate(id: string): TemplateInstance {
  if (process.env.NODE_ENV === "production") throw new Error("The internal template catalog is unavailable in production.");
  const template = REPORT_TEMPLATES.find(item => item.id === id);
  if (!template) throw new Error("Unknown template identifier.");
  const compatibility = templateCompatibility(template);
  if (!compatibility.supported) throw new Error(compatibility.messages.join("; "));
  const payload: FormattedInvoicePayload & JsonRecord = cloneValue(template.definition);
  payload.definitionId = `template-report-${crypto.randomUUID()}`;
  payload.definitionVersion = 1;
  delete payload.overrideOf;
  delete payload.revision;
  delete payload.definitionRevision;
  delete payload.reportKey;
  instances.set(payload, template.id);
  return { payload, provenance: Object.freeze({ templateId: template.id, templateRevision: template.revision, origin: template.origin }) };
}
export function templatePreviewOptions(payload: FormattedInvoicePayload, values?: Readonly<Record<string, unknown>>) {
  const admission = assertReportAdmission(payload, "validate");
  return admission.requiredFeatures.length ? { typedExecution: createInternalTypedExecution(payload, values) } : {};
}
export function validateTemplateCatalog(templates: readonly ReportTemplate[] = REPORT_TEMPLATES): void {
  const ids = new Set<string>();
  for (const template of templates) {
    if (ids.has(template.id)) throw new Error(`Duplicate template ID ${template.id}.`);
    ids.add(template.id);
    const compatibility = templateCompatibility(template);
    if (!compatibility.supported) throw new Error(`${template.id}: ${compatibility.messages.join("; ")}`);
    const options = templatePreviewOptions(template.definition);
    const plan = resolveNativeLayoutPlan(template.definition, options);
    resolveNativeContentPlan(template.definition, options);
    if (plan.overflow.length) throw new Error(`${template.id}: Preview has layout overflow.`);
  }
}
